import { Component } from '@theme/component';
import { prefersReducedMotion, startViewTransition } from '@theme/utilities';

// Minimum deliberate horizontal drag, in px, to count as a swipe rather than
// an incidental tap or a vertical page scroll.
const SWIPE_THRESHOLD_PX = 40;

/**
 * Timeline.
 *
 * Switches which `_timeline-item` panel is visible (a tab switch, not an
 * accordion — exactly one panel shows at a time) and keeps each panel's own
 * media gallery in sync: which slide is scrolled into view, which thumbnail
 * reads as current, and which video (if any) is playing. Switching item
 * always resets its gallery back to the first slide (spec).
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} scrollers - One per item, the horizontally
 *   scrolling strip of `.timeline__slide-wrap` elements rendered by that
 *   item's `_timeline-media` children.
 *
 * @extends {Component<Refs>}
 */
export class TimelineComponent extends Component {
  /** @type {Map<Element, number>} */
  #frames = new Map();
  #controller = new AbortController();
  #desktopQuery = window.matchMedia('(min-width: 1024px)');
  #gestureStartX = 0;
  #gestureStartY = 0;
  // Whichever scroll edge(s) the gesture's own scroller was already resting
  // at when the gesture began — captured at touchstart, not re-read at
  // touchend, so a swipe that itself scrolls a middle slide all the way to
  // the last one doesn't *also* count as "was already at the edge" and
  // advance the panel in the same gesture. A single-slide gallery has no
  // scroll range at all, so both are always true there — see
  // `#handleGalleryTouchEnd`.
  #gestureAtStart = false;
  #gestureAtEnd = false;
  // Only tracks `.timeline__nav`'s own width (a viewport/orientation change,
  // same as assets/header-menu.js's matching observer) — appending the
  // triggers themselves in `#build()` doesn't resize `nav`'s own box, so
  // that initial state is instead synced by one direct call right after.
  /** @type {ResizeObserver | null} */
  #navResizeObserver = null;
  // Serializes `switchItem` calls so at most one view transition is ever in
  // flight at a time — see that method's own comment for why overlapping
  // ones silently break. Settles to a no-op resolved promise once idle.
  /** @type {Promise<void>} */
  #transitionQueue = Promise.resolve();

  connectedCallback() {
    super.connectedCallback();

    this.#build();
    this.#syncNavOrientation();

    const { signal } = this.#controller;

    const nav = this.querySelector('.timeline__nav');
    if (nav) {
      nav.addEventListener('scroll', this.#syncNavScrollMask, { passive: true, signal });
      this.#navResizeObserver = new ResizeObserver(this.#syncNavScrollMask);
      this.#navResizeObserver.observe(nav);
    }
    this.#syncNavScrollMask();

    // No `requiredRefs`: a section with no items yet (freshly added in the
    // editor, before any `_timeline-item` block exists) has no scrollers at
    // all — that is a valid, if empty, state rather than a bug.
    for (const scroller of this.refs.scrollers ?? []) {
      scroller.addEventListener('scroll', () => this.#onScroll(scroller), { passive: true, signal });
      this.#syncActiveSlide(scroller);

      // Mobile-only swipe (spec): a gallery with only one slide has no
      // scroll range of its own, so any qualifying swipe over it moves to
      // the next/previous item instead. A gallery with more than one slide
      // keeps its native scroll-snap swipe for browsing slides, and only
      // moves to the next/previous item once a swipe happens while already
      // resting at that gallery's last/first slide — see
      // `#handleGalleryTouchEnd`. Bound per scroller (rather than once on
      // the component) since each item's edge state is its own.
      //
      // Real `touchstart`/`touchend`, not `pointerdown`/`pointerup`: once the
      // browser recognizes a touch as a native horizontal pan over this
      // scroller (its `touch-action` allows it, unlike
      // assets/editorial-product-stack.js's own swipe target), it cancels
      // that pointer's event sequence (`pointercancel`, never `pointerup`)
      // to hand the gesture to native scrolling — the underlying touch
      // events keep firing regardless, so only they can reliably tell us
      // where the gesture actually ended.
      scroller.addEventListener('touchstart', this.#handleGalleryTouchStart, { signal, passive: true });
      scroller.addEventListener('touchend', this.#handleGalleryTouchEnd, { signal, passive: true });
    }

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.#desktopQuery.addEventListener('change', this.#syncNavOrientation, { signal });

    // In the theme editor, switch to the relevant item (and, for a media
    // block, its slide) when it is selected. Same `signal`-based cleanup as
    // tabs.js — without it, a `document`-level listener from a detached
    // instance (the editor replaces the whole component on setting changes)
    // would keep that instance's DOM/closures alive indefinitely.
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#navResizeObserver?.disconnect();
    this.#navResizeObserver = null;

    for (const frame of this.#frames.values()) cancelAnimationFrame(frame);
    this.#frames.clear();
  }

  /** @returns {HTMLElement[]} */
  get triggers() {
    return Array.from(this.querySelectorAll(':scope .timeline__trigger'));
  }

  /** @returns {HTMLElement[]} */
  get panels() {
    return Array.from(this.querySelectorAll(':scope > .timeline__grid > .timeline__panel'));
  }

  /**
   * Moves every item's nav trigger (rendered next to its panel by
   * blocks/_timeline-item.liquid) into the shared `.timeline__nav` — same
   * relocation technique as tabs.js/category-carousel — and, per item, each
   * of its `_timeline-media` blocks' thumbnail buttons into that item's own
   * `.timeline__thumbs` row (see the section stylesheet for why the row,
   * not each thumbnail, is what needs to escape the scroller).
   */
  #build() {
    const nav = this.querySelector('.timeline__nav');
    if (!nav) return;

    for (const trigger of this.triggers) {
      nav.appendChild(trigger);
    }

    for (const scroller of this.refs.scrollers ?? []) {
      const thumbs = this.#thumbsFor(scroller);
      if (!thumbs) continue;

      for (const thumb of scroller.querySelectorAll('.timeline__thumb')) {
        thumbs.appendChild(thumb);
      }
    }

    this.setAttribute('data-ready', '');
  }

  /**
   * Keeps `.timeline__nav`'s `aria-orientation` matching its actual layout —
   * a horizontal anchor bar on mobile, a vertical list on desktop (the
   * 1024px breakpoint in the section's stylesheet) — so a screen reader
   * announces the right arrow keys for the tablist pattern.
   */
  #syncNavOrientation = () => {
    const nav = this.querySelector('.timeline__nav');
    if (nav) nav.setAttribute('aria-orientation', this.#desktopQuery.matches ? 'vertical' : 'horizontal');
  };

  /**
   * Removes the mobile scroll-fade mask (section stylesheet) once
   * `.timeline__nav` has nothing left to scroll to on its right — either it
   * never overflowed in the first place, or the strip is already scrolled to
   * its last trigger — so it never fades content that isn't actually hiding
   * anything past it. Same `<= 1` rounding tolerance as
   * assets/header-menu.js's own scroll-button visibility check.
   */
  #syncNavScrollMask = () => {
    const nav = this.querySelector('.timeline__nav');
    if (!nav) return;

    const atEnd = nav.scrollWidth - nav.clientWidth - nav.scrollLeft <= 1;
    nav.toggleAttribute('data-nav-scroll-end', atEnd);
  };

  /**
   * Activates an item: selects its trigger, shows its panel, hides the
   * rest, and resets its gallery to the first slide.
   *
   * On desktop (see the `view-transition-name` declarations in the section's
   * stylesheet — mobile assigns none there, so the transition is a no-op),
   * the switch runs inside a view transition so the panel's
   * headline/subtitle/text crossfade in on a stagger (spec: a plain fade,
   * not a slide — see the stylesheet comment for why a directional slide was
   * dropped).
   *
   * Queued behind `#transitionQueue` rather than run immediately: the View
   * Transition API only ever allows one active transition per document —
   * starting a second one before the first's `finished` promise settles
   * (e.g. switching rapidly) makes the browser silently skip the first with
   * no animation at all, and would also strip `data-transitioning` (needed
   * for the *second*, still-running transition's own `view-transition-name`
   * rules) the moment the first one's `.finally()` fires. Chaining onto the
   * queue instead defers each switch's own DOM reads until its actual turn,
   * so every switch still resolves against fresh state and gets to play its
   * own transition in full.
   *
   * Returns a promise that resolves once the switch (transition included)
   * has fully applied — `#handleBlockSelect` awaits it before selecting a
   * gallery slide, so it never lands on a panel that hasn't been shown yet.
   * @param {string} [id]
   * @param {Event} [event]
   * @returns {Promise<void>}
   */
  switchItem(id, event) {
    event?.preventDefault();
    if (!id) return Promise.resolve();

    const run = () => {
      const triggers = this.triggers;
      const trigger = triggers.find((item) => item.dataset.itemId === id);
      const panel = this.panels.find((item) => item.dataset.itemId === id);
      if (!trigger || !panel) return Promise.resolve();

      const activeTrigger = triggers.find((item) => item.getAttribute('aria-selected') === 'true');
      const alreadyActive = activeTrigger === trigger;

      const apply = () => {
        const previousPanel = this.panels.find((item) => !item.hidden);

        for (const item of triggers) {
          const selected = item === trigger;
          item.setAttribute('aria-selected', selected ? 'true' : 'false');
          item.tabIndex = selected ? 0 : -1;
        }

        for (const item of this.panels) {
          item.hidden = item !== panel;
        }

        // `hidden` alone doesn't pause an HTMLMediaElement — without this the
        // outgoing panel's video keeps playing/decoding invisibly.
        if (previousPanel && previousPanel !== panel) {
          for (const video of previousPanel.querySelectorAll('video')) {
            video.pause();
          }
        }

        const scroller = panel.querySelector('.timeline__scroller');
        if (scroller instanceof HTMLElement) this.#resetGallery(scroller);
      };

      if (!activeTrigger || alreadyActive) {
        apply();
        return Promise.resolve();
      }

      // Scopes the view-transition-name rules (section stylesheet) to this
      // instance only, for as long as its transition is in flight — see the
      // comment there for why an unconditional name would collide with a
      // second Timeline section on the same page.
      this.setAttribute('data-transitioning', '');
      return startViewTransition(apply).finally(() => this.removeAttribute('data-transitioning'));
    };

    const queued = this.#transitionQueue.then(run, run);
    this.#transitionQueue = queued.catch(() => {});
    return queued;
  }

  /**
   * Activates one gallery slide by the id of its `_timeline-media` block —
   * used by both a thumbnail click and the editor's block-select handling.
   * @param {string} [id]
   * @param {Event} [event]
   */
  selectMedia(id, event) {
    event?.preventDefault();
    if (!id) return;

    const slide = this.querySelector(`.timeline__slide[data-media-id="${id}"]`);
    const slideWrap = slide?.closest('.timeline__slide-wrap');
    const scroller = slideWrap?.closest('.timeline__scroller');
    if (!(slideWrap instanceof HTMLElement) || !(scroller instanceof HTMLElement)) return;

    slideWrap.scrollIntoView({
      inline: 'center',
      block: 'nearest',
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
    });
    this.#setActiveSlide(scroller, slideWrap);
  }

  /**
   * @param {HTMLElement} scroller
   */
  #onScroll(scroller) {
    if (this.#frames.has(scroller)) return;

    this.#frames.set(
      scroller,
      requestAnimationFrame(() => {
        this.#frames.delete(scroller);
        this.#syncActiveSlide(scroller);
      }),
    );
  }

  /**
   * Reads which slide is currently scrolled into view and syncs thumbnails
   * and video playback to it. Shared by the scroll listener (mobile swipe)
   * and the initial state on connect.
   * @param {HTMLElement} scroller
   */
  #syncActiveSlide(scroller) {
    const index = scroller.clientWidth ? Math.round(scroller.scrollLeft / scroller.clientWidth) : 0;
    const slideWrap = scroller.children[index];
    if (slideWrap instanceof HTMLElement) this.#setActiveSlide(scroller, slideWrap);
  }

  /**
   * @param {HTMLElement} scroller
   */
  #resetGallery(scroller) {
    scroller.scrollTo({ left: 0, behavior: 'instant' });

    const first = scroller.children[0];
    if (first instanceof HTMLElement) this.#setActiveSlide(scroller, first);
  }

  /**
   * @param {HTMLElement} scroller
   * @param {HTMLElement} activeSlideWrap
   */
  #setActiveSlide(scroller, activeSlideWrap) {
    const thumbs = this.#thumbsFor(scroller);

    for (const slideWrap of scroller.children) {
      if (!(slideWrap instanceof HTMLElement)) continue;

      const isActive = slideWrap === activeSlideWrap;
      slideWrap.toggleAttribute('data-active', isActive);
      this.#setPlaying(slideWrap.querySelector('video'), isActive);

      // The thumbnail for this slide no longer lives inside it — #build
      // relocated it into `thumbs` — so it's matched back up by the
      // `data-media-id` the slide and its thumbnail share.
      const mediaId = slideWrap.querySelector('.timeline__slide')?.getAttribute('data-media-id');
      const thumb = mediaId && thumbs ? thumbs.querySelector(`.timeline__thumb[data-media-id="${mediaId}"]`) : null;
      thumb?.setAttribute('aria-current', isActive ? 'true' : 'false');
    }
  }

  /**
   * @param {HTMLElement} scroller
   * @returns {HTMLElement | null}
   */
  #thumbsFor(scroller) {
    return scroller.parentElement?.querySelector(':scope > .timeline__thumbs') ?? null;
  }

  /**
   * @param {Element | null} video
   * @param {boolean} shouldPlay
   */
  #setPlaying(video, shouldPlay) {
    if (!(video instanceof HTMLVideoElement)) return;

    // `connectedCallback` syncs every item's gallery on load, including the
    // ones whose panel starts `hidden` — skip those rather than playing a
    // video the shopper can't see.
    const panel = video.closest('.timeline__panel');
    const panelHidden = panel instanceof HTMLElement && panel.hidden;

    if (shouldPlay && !prefersReducedMotion() && !panelHidden) {
      // Rapid thumbnail toggles (or an immediate pause under iOS Low Power
      // Mode) can reject this — matches hero-slider.js's own guard.
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }

  /**
   * @param {TouchEvent} event
   */
  #handleGalleryTouchStart = (event) => {
    const touch = event.touches[0];
    if (!touch || !(event.currentTarget instanceof HTMLElement)) return;

    const scroller = event.currentTarget;
    this.#gestureStartX = touch.clientX;
    this.#gestureStartY = touch.clientY;
    this.#gestureAtStart = scroller.scrollLeft <= 1;
    this.#gestureAtEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 1;
  };

  /**
   * Native scroll-snap already handled any in-gallery movement by the time
   * this fires — this only ever decides whether the *gesture itself*, not
   * the resulting scroll position, also crosses a gallery edge that was
   * already resting there before the drag started.
   * @param {TouchEvent} event
   */
  #handleGalleryTouchEnd = (event) => {
    if (this.#desktopQuery.matches) return;

    // The lifted touch is gone from `touches` by this point — `changedTouches`
    // is where its final position lives.
    const touch = event.changedTouches[0];
    if (!touch) return;

    const dx = touch.clientX - this.#gestureStartX;
    const dy = touch.clientY - this.#gestureStartY;
    // A drag that's more vertical than horizontal is left alone so the page
    // can still scroll.
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX || Math.abs(dx) <= Math.abs(dy)) return;

    if (dx < 0 && this.#gestureAtEnd) this.#advance(1);
    else if (dx > 0 && this.#gestureAtStart) this.#advance(-1);
  };

  /**
   * Moves to the next (`1`) or previous (`-1`) item, wrapping at either end —
   * same wraparound as `#handleKeyDown`'s own arrow-key navigation. Unlike a
   * direct tap or arrow key, this never itself puts the newly active trigger
   * in front of the shopper first — a tap only ever lands on one already
   * visible, and an arrow key moves browser focus there, which scrolls it
   * into view on its own — so this scrolls the mobile nav strip
   * (`.timeline__nav`'s own horizontal scroll) to it explicitly, the same
   * way `selectMedia` already does for a gallery slide. A no-op on desktop:
   * the nav there is a plain non-scrolling vertical list (`overflow: visible`
   * in the section stylesheet), so there's nothing to scroll.
   * @param {1 | -1} direction
   */
  #advance(direction) {
    const triggers = this.triggers;
    const activeTrigger = triggers.find((item) => item.getAttribute('aria-selected') === 'true');
    const index = activeTrigger ? triggers.indexOf(activeTrigger) : -1;
    if (index === -1) return;

    const next = triggers[(index + direction + triggers.length) % triggers.length];
    if (!next) return;

    next.scrollIntoView({ inline: 'center', block: 'nearest', behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
    this.switchItem(next.dataset.itemId);
  }

  /**
   * Arrow key navigation across nav triggers, following the ARIA tabs
   * pattern (same as tabs.js). Unlike tabs.js, the nav is horizontal on
   * mobile but a vertical list on desktop (1024px breakpoint), so both key
   * pairs are accepted at every width rather than switching on
   * `#desktopQuery` — a vertical-only listener would miss Up/Down.
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('.timeline__trigger');
    if (!(trigger instanceof HTMLElement)) return;

    const triggers = this.triggers;
    const index = triggers.indexOf(trigger);
    if (index === -1) return;

    let next;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = triggers[(index + 1) % triggers.length];
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = triggers[(index - 1 + triggers.length) % triggers.length];
        break;
      case 'Home':
        next = triggers[0];
        break;
      case 'End':
        next = triggers[triggers.length - 1];
        break;
      default:
        return;
    }

    event.preventDefault();
    this.switchItem(next.dataset.itemId);
    next.focus();
  };

  /**
   * @param {Event} event
   */
  #handleBlockSelect = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;

    const trigger = event.target.closest('.timeline__trigger');
    if (trigger instanceof HTMLElement && trigger.dataset.itemId) {
      this.switchItem(trigger.dataset.itemId);
      return;
    }

    const slideWrap = event.target.closest('.timeline__slide-wrap');
    if (!(slideWrap instanceof HTMLElement)) return;

    const panel = slideWrap.closest('.timeline__panel');
    const mediaId = slideWrap.querySelector('.timeline__slide')?.getAttribute('data-media-id');
    if (!mediaId) return;

    // Waits for switchItem's panel-visibility change (and, on desktop, its
    // view transition) to actually land before selecting the slide —
    // otherwise, for a block in a not-yet-active item, selectMedia would run
    // against a still-hidden panel and get clobbered when the deferred
    // switch later resets the gallery back to slide 1.
    const switched =
      panel instanceof HTMLElement && panel.dataset.itemId ? this.switchItem(panel.dataset.itemId) : Promise.resolve();
    switched.then(() => this.selectMedia(mediaId));
  };
}

if (!customElements.get('timeline-component')) {
  customElements.define('timeline-component', TimelineComponent);
}
